import { artworkPruefungAnstossen, artworkPruefungGesehen } from "../../../../api/artworkPruefungen.ts";
import { KonfliktFehler } from "../../../../api/basis.ts";
import type { Melder } from "./melder.ts";

/**
 * Satz fuer ein gelungenes Schreiben, dessen Nachladen scheiterte (Spec R4): gespeichert ist es, nur die
 * Anzeige ist veraltet. Die Ursache des Nachladens haengt `meldeNachladeFehler` an - ausser sie raet zum Wiederholen
 * (R-Z2): ein "erneut versuchen" hinter "hochgeladen" liesse den Nutzer eine zweite Datei hochladen.
 */
export const anzeigeVeraltet = (zustand: string): string =>
  `${zustand}, die Anzeige konnte aber nicht aktualisiert werden. Bitte neu laden.`;

/**
 * Die beiden Handlungen an einer Artwork-Prüfung - „Prüfen“ / „Erneut prüfen“ und „Gesehen“ -, so wie sie der Reiter Dokumente
 * (`Positionsdokumente.tsx`) und die Zusammenfassung des Massenuploads gleich brauchen: ein Weg, eine Meldung. Beide lehnen nie ab;
 * ein Fehler geht in den Fehlerstapel, mit dem Satz, der zu dem passt, was davor gelang.
 *
 * `neuLaden` ist das `neuLaden` von `useArtworkPruefungen`. Es lädt den Prüfstand nach einer gelungenen Aktion neu; scheitert das,
 * steht die Aktion trotzdem (Spec R4, wie `nachladen` im Reiter): die Meldung sagt, was gespeichert ist, und haengt die Ursache an -
 * nur ohne Wiederholungsrat.
 *
 * `bestellungNachladen` lädt die Bestellung in den Store nach (`App.tsx#dokumentBestellungNachladen`, derselbe Weg wie nach einem
 * Dokumentupload): die Board-Marke „Artwork prüfen“ liest `artworkWarnungen` von der Bestellung, und die ändert sich mit „Gesehen“
 * und „Erneut prüfen“. Aufgerufen wird es nur nach einer gelungenen Aktion (auch nach dem 409 von „Gesehen“ nicht: dort hat nichts
 * geschrieben), gemeinsam mit `neuLaden`. Scheitert eines von beiden, gilt dieselbe Meldung: gespeichert ist die Aktion, nur die
 * Anzeige (Reiter, Dialog oder Board) ist veraltet.
 *
 * `melder` kommt vom Aufrufer, der den Fehlerstapel selbst importiert (Gründe in `melder.ts`).
 */
export function pruefAktionen(
  orderId: string,
  neuLaden: () => Promise<void>,
  melder: Melder,
  bestellungNachladen?: () => Promise<void>,
): {
  anstossen: (linkId: string) => Promise<void>;
  alsGesehen: (linkId: string) => Promise<void>;
} {
  /** `mitBestellung`: auch die Bestellung im Store nachladen (nur nach einer gelungenen Aktion). */
  const nachladen = async (zustand: string, mitBestellung: boolean): Promise<void> => {
    try {
      await Promise.all([neuLaden(), mitBestellung ? bestellungNachladen?.() : undefined]);
    } catch (ursache) {
      console.error("Das Nachladen nach der Artwork-Prüfung ist gescheitert:", ursache);
      melder.meldeNachladeFehler(ursache, anzeigeVeraltet(zustand));
    }
  };

  /** „Prüfen“ / „Erneut prüfen“: reiht die Prüfung ein; das Ergebnis kommt über den Prüfstand (Polling), nicht aus der Antwort. */
  const anstossen = async (linkId: string): Promise<void> => {
    try {
      await artworkPruefungAnstossen(orderId, linkId);
    } catch (ursache) {
      console.error("Die Artwork-Prüfung konnte nicht angestoßen werden:", ursache);
      melder.meldeFehler(ursache, "Die Artwork-Prüfung konnte nicht angestoßen werden.");
      return;
    }
    await nachladen("Die Prüfung ist eingereiht", true);
  };

  /**
   * „Gesehen“: nimmt die Warnung aus dem Board und vermerkt, wer sie wann gesehen hat. Der Dialog bleibt offen und zeigt es.
   * Bei 409 (jemand hat sie schon gesehen, oder eine neue Prüfung läuft) ist der Dialog veraltet: nach der Meldung neu laden,
   * damit er „Gesehen“ nicht weiter anbietet.
   */
  const alsGesehen = async (linkId: string): Promise<void> => {
    try {
      await artworkPruefungGesehen(orderId, linkId);
    } catch (ursache) {
      console.error("Die Warnung konnte nicht als gesehen markiert werden:", ursache);
      melder.meldeFehler(ursache, "Die Warnung konnte nicht als gesehen markiert werden.");
      if (ursache instanceof KonfliktFehler) await nachladen("Die Prüfung hat sich inzwischen geändert", false);
      return;
    }
    await nachladen("Die Warnung ist als gesehen vermerkt", true);
  };

  return { anstossen, alsGesehen };
}
